// Sales-order pallet beacons at Laser Start (phase B, 2026-10-22; jobq-api contract
// docs/so-pallet-beacons-contract.md). POST /tasks/{id}/start answers `pallet_beacons` when the
// switch is on and the task routes onto pallets; the queue shows it in a popup
// (utils/palletBeaconsPrompt.js, through ui.palletBeacons) that says, per pallet, which one to get
// and which beacon is flashing for it. Nobody scans anything: OK only turns the lights off.
//
// The words and the three calls live here (pure / never throw), so the desktop and the phone
// popup are the same one and the tests can drive them without a screen.
import { beaconFetch } from "../../utils/beacons";

/** The answer of a pallet-beacon call: {ok, status, body, message, error}. `error` = the API's
 *  code ('no_free_beacon', 'has_beacon', ...) from a 409 `{detail: {error, message}}` (or a bare
 *  `{error, message}`), else null. Never throws. */
async function call(path, body) {
  const r = await beaconFetch(path, { method: "POST", body: JSON.stringify(body) });
  if (r.ok) return { ...r, error: null };
  const b = r.body && typeof r.body === "object" ? r.body : {};
  const d = b.detail && typeof b.detail === "object" ? b.detail : null;
  const error = (d && d.error) || b.error || null;
  const message = (d && d.message) || (!d && b.message) || r.message;
  const freeNote = (d && d.free_note) || null;
  return { ...r, error, message, free_note: freeNote };
}

/** GET /tasks/{id}/pallet_beacons -> {task_id, pallets} as they are now (the popup reopened from the task;
 *  `pallets: []` when the task's parts go on no pallet, or the sales-order beacons are switched off). */
export async function loadTaskPallets(taskId) {
  const r = await beaconFetch(`/tasks/${encodeURIComponent(taskId)}/pallet_beacons`);
  return { ...r, error: null };
}

/** POST /tasks/{id}/pallet_beacons/lights {action: 'off' | 'again'} -> {task_id, pallets}. */
export function taskPalletLights(taskId, action, { palletIds = null, actor = null } = {}) {
  const body = { action, actor };
  if (Array.isArray(palletIds) && palletIds.length) body.pallet_ids = palletIds;
  return call(`/tasks/${encodeURIComponent(taskId)}/pallet_beacons/lights`, body);
}

/** POST /tasks/{id}/pallets/{pallet_id}/beacon {action: 'assign' | 'swap'} -> {task_id, pallet}. */
export function taskPalletBeacon(taskId, palletId, action, { actor = null } = {}) {
  return call(`/tasks/${encodeURIComponent(taskId)}/pallets/${encodeURIComponent(palletId)}/beacon`, { action, actor });
}

/** A station after the laser (a name; anything else, e.g. Array.map's index, is the Laser). */
export function isDownstream(station) {
  return typeof station === "string" && station.trim() !== "" && station !== "Laser";
}

/**
 * What a pallet row is in the popup:
 *   'new'          a new pallet with a beacon to clip on (flashing fast in the beacon bin)
 *   'no_free'      a new pallet and no free beacon: print a pallet label
 *   'find'         a pallet already in progress with its beacon (flashing): bring it to the laser
 *   'label_attach' in progress, a beacon was just put on it (flashing in the bin): clip it on
 *   'label'        in progress with no beacon: find it by its printed label
 */
export function palletRowKind(p, station = "Laser") {
  if (!p) return "label";
  // a station after the laser only finds pallets: one with a beacon is FIND, one without is LABEL
  if (isDownstream(station)) return p.beacon_id ? "find" : "label";
  if (p.kind === "new") return p.beacon_id ? "new" : "no_free";
  if (!p.beacon_id) return "label";
  return p.light === "attach" ? "label_attach" : "find";
}

/** The pill on a row: NEW / FIND / LABEL. */
export function palletRowPill(kind) {
  if (kind === "new" || kind === "no_free") return "NEW";
  if (kind === "find") return "FIND";
  return "LABEL";
}

/** "40 × 48" from the pallet's size (length_in / width_in, else its `size` text), or "". */
export function palletSizeText(p) {
  const l = Number(p && p.length_in);
  const w = Number(p && p.width_in);
  if (l > 0 && w > 0) return `${Math.round(l)} × ${Math.round(w)}`;
  const s = p && typeof p.size === "string" ? p.size.trim() : "";
  return s ? s.replace(/\s*[xX×]\s*/, " × ") : "";
}

/** The row's sentence (after the pallet's PLT id). `station`: the task's station (a station after the laser has
 *  its own words: the pallet is brought to it). */
export function palletRowText(p, station = "Laser") {
  if (isDownstream(station)) return downstreamRowText(p, station);
  const kind = palletRowKind(p);
  const plt = (p && (p.short_id || p.label)) || "Pallet";
  const size = palletSizeText(p);
  switch (kind) {
    case "new":
      // only the attach light means "clip it on"; reopened later it just names the beacon (JobQ can't know
      // whether it was clipped on yet)
      if (p.beacon_away) return `${plt} · its beacon ${p.beacon_id} is on the shipping shelf (the pallet is at the vendor).`;
      if (p.light === "attach") {
        return `${plt} · get a ${size ? `${size} ` : ""}pallet and clip on ${p.beacon_id}, flashing fast in the beacon bin.`;
      }
      return `${plt} · a ${size ? `${size} ` : ""}pallet; its beacon is ${p.beacon_id}${p.light ? ", flashing" : " (Find lights it)"}.`;
    case "no_free":
      return `${plt} · get a ${size ? `${size} ` : ""}pallet. No free beacon for it: print a pallet label, or try again.`;
    case "find":
      if (p.beacon_away) return `${plt} · at the vendor; its beacon ${p.beacon_id} is on the shipping shelf.`;
      return p.light
        ? `${plt} · in progress. Bring it to the laser; ${p.beacon_id} is flashing.`
        : `${plt} · in progress, ${p.beacon_id} on it. Find lights it.`;
    case "label_attach":
      return `${plt} · in progress. Find it by its printed label and clip on ${p.beacon_id}, flashing fast in the beacon bin.`;
    default:
      return `${plt} · in progress, no beacon on it. Find it by its printed label.`;
  }
}

/** A pallet row at a station after the laser (2026-10-26). */
export function downstreamRowText(p, station) {
  const plt = (p && (p.short_id || p.label)) || "Pallet";
  const where = station || "your station";
  if (!p || !p.beacon_id) return `${plt} · no beacon on it. Find it by its printed label.`;
  if (p.beacon_away) return `${plt} · at the vendor; its beacon ${p.beacon_id} is on the shipping shelf.`;
  if (p.light && p.light_mine === false) return `${plt} · ${p.beacon_id} is flashing for another task right now.`;
  return p.light
    ? `${plt} · ${p.beacon_id} is flashing. Bring the pallet to ${where}.`
    : `${plt} · ${p.beacon_id} on it. Find lights it.`;
}

/** "Laser task 40112 · SO-1144 · Faith Technologies · 3 pallets" (several orders: each named once). */
export function palletBeaconsSubline(data, taskId = null) {
  const pallets = (data && Array.isArray(data.pallets)) ? data.pallets : [];
  const id = (data && data.task_id) || taskId;
  const uniq = (key) => [...new Set(pallets.map((p) => p && p[key]).filter((x) => x && String(x).trim()))];
  const n = pallets.length;
  return [
    id ? `Laser task ${id}` : "Laser task",
    uniq("so_number").join(", "),
    uniq("customer").join(", "),
    `${n} pallet${n === 1 ? "" : "s"}`,
  ].filter(Boolean).join(" · ");
}

/** A row whose beacon can be lit again from the popup (one pallet's Find): it has a beacon, not on the shipping
 *  shelf while its pallet is at a vendor (that would light the shelf). */
export function palletRowCanFind(p) {
  return !!(p && p.beacon_id && !p.beacon_away);
}

/** Where the row's beacon is: the Find's newest answer (its post-Find scan) wins over the pallet entry's
 *  last known location. null when neither has one. */
export function palletRowLocation(p, progress) {
  if (progress && progress.location) return progress.location;
  return (p && p.location) || null;
}

/** The answer to "why no free beacon": the API's free_note (the row, or a refused Try again), else a plain
 *  line. */
export function freeBeaconNote(p, refusal = null) {
  const note = (refusal && refusal.free_note) || (p && p.free_note) || "";
  return typeof note === "string" ? note.trim() : "";
}

/** "FAB31162 · 224" per part line. */
export function palletPartsText(p) {
  const parts = p && Array.isArray(p.parts) ? p.parts : [];
  return parts.map((x) => `${x.part_number || `Part ${x.part_id}`} · ${x.qty}`);
}

/**
 * The light on a row, in a few words, from its Find's progress (components/FindProgress
 * useFindProgress on the row's command_id): {tone, text, busy}.
 *   no command: "Light off" when the pallet has a beacon, else nothing
 *   queued / sent / no answer yet: "Sending to BCN-012…"
 *   lit: "BCN-012 is lit"
 *   failed / expired: "Couldn't light BCN-012"
 */
export function palletLightWords(p, progress) {
  const id = (p && p.beacon_id) || "the beacon";
  if (!p || !p.command_id) {
    return p && p.beacon_id ? { tone: "secondary", text: "Light off", busy: false } : { tone: "secondary", text: "", busy: false };
  }
  const phase = progress ? progress.phase : null;
  if (phase === "lit") return { tone: "success", text: `${id} is lit`, busy: false };
  if (phase === "failed" || phase === "expired") return { tone: "danger", text: `Couldn't light ${id}`, busy: false };
  if (phase === "cancelled" || phase === "gone" || phase === "stale") return { tone: "secondary", text: "", busy: false };
  return { tone: "primary", text: `Sending to ${id}…`, busy: true };
}

/** Put a refreshed pallet entry into the list in place (by pallet_id). */
export function replacePallet(pallets, entry) {
  if (!entry || !entry.pallet_id) return pallets;
  return (pallets || []).map((p) => (p.pallet_id === entry.pallet_id ? entry : p));
}
